import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import DOMPurify from 'dompurify';
import Ravelin from 'ravelinjs/core+encrypt';
import FetchService from '../../services/fetchService';
import authService from '../../services/authService';
import urlService from '../../services/urlService';
import { isArray } from '../../utils/getTypeofItem';
import { getOS, getUserAgent, getBrowserVersion, isAndroid, isiOS15Safari } from '../../utils/getUserAgent';
import { updateFloatDigits } from '../../utils/updateFloatDigits';
import BookerContext, { BookerProvider } from '../../context/bookerContext';
import BookerShortContext from '../../context/bookerShortContext';
import store from '../../store/bookerStore';
import fieldParams from '../../const/fieldParams';
import InputKeyList from '../../const/inputKeyList';
import { clearMapboxgl } from '../../services/mapboxService';
import { getToken } from '../../services/tokenService';
import { useValidatedInput } from '../../hooks/useValidatedInput';
import FieldControl from '../InputControls/FieldControl';
import Payments from './Payments';
import Loader from '../Loader';
import Popup from '../Popup';
import RideHeader from './RideHeader';
import Map from '../Tracker/Map';
import RideFooter from './RideFooter';
import SlideUpScreen from './SlideUpScreen';
import NewFareEstimate from './NewFareEstimate';
import Receipt from './Receipt';
import PaymentFailure from './PaymentFailure';
import Processing from './Processing';
import ActiveRideDetails from './ActiveRideDetails';
import EditPayment from './EditPayment';
import { ReactComponent as LogoIcon } from '../../assets/img/curb-logo.svg';
import { ReactComponent as ArrowIcon } from '../../assets/img/arrow.svg';
import { ReactComponent as CloseIcon } from '../../assets/img/close.svg';
import '../../assets/styles/custom.css';
import '../../assets/styles/booker-base.sass';
import '../../assets/styles/booker-advanced.sass';
import { mixpanelEventTrack, registerMixpanelEvent } from '../../services/mixpanel';
import PaymentList from '../InputControls/PaymentList';
import RideStatus from '../RideComponents/RideStatus';
import PaymentMethods from './PaymentMethods';
import loadScript from '../../utils/loadScript';
import jwtDecode from 'jwt-decode';
import { getPayInst } from '../../services/paymentService';
import { cardParams, applePayObj, payPalObj, styles } from '../../const/cardData';
import MapContext from '../../context/mapContext';
import addPayments from '../../utils/addPayments';
import { updatePaymentDescription } from '../../utils/updatePaymentDescription';
import Menu from './Menu';
import { saveApplePayPayment, savePayPalPayment, getPaymentTotalData } from '../../utils/savePayments';
import RideHistory from '../Menu/RideHistory';
import RideDetailsHistory from '../Menu/RideDetailsHistory';
import RideDetailsReceipt from '../Menu/RideDetailsReceipt';
import { isEmptyObject } from '../../utils/valueType';
import { isStorageItem } from '../../utils/isStorageItem';
import { isProd } from '../../const/baseUrl';
import { getCookieValue } from '../../utils/cookieValue';
import TipDetails from './TipDetails';
import parseISO from 'date-fns/parseISO';
import add from 'date-fns/add';

const Booker = () => {
  const {
    state,
    updateIsWebBooker,
    updateStep,
    updateCode,
    updatePickup,
    updateDropoff,
    updatePayment,
    updateFare,
    updateRideId,
    updateEditor,
    updateError,
    updatePaymentMethodId,
    updateIsRideStartedPopup,
    updateRideStatusRecordedFlags,
    updateCountry,
    updateAccountCreation,
    updateDownloadCurbAppUrl,
    updateServiceTypesAndAmounts,
    updateDefaultServiceType,
    updatePreviousStep,
    updateIsCurbUserExist,
    updateInst,
    updateUserId,
    updateDriverNote,
    updateFleets,
    updateSelectedFleet,
    updateApplePayPaymentMethod,
    updateUserProfile,
    updateInitialRideEta,
    updatePayPalPaymentMethod,
    updateKeepSignedIn,
    clearBookerContext,
    updateRideDetailsHistory,
    updateRideDetailsReceipt,
    updatePaymentListId,
    updateOneTimePaymentId,
    updateDefaultTips,
    updateTipsValue,
    updateUserLocation,
    updateRideLater,
    updateIsUpcomingRidePopup,
    updateStartingRideTime,
    updateCustomToken,
    updateDeviceId,
    updateIsMeteredFare,
    updateMixpanelEvents,
  } = useContext(BookerContext);
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams({});
  const alias = searchParams.get('alias');
  const customTokenFromURL = searchParams.get('token');
  const context = useContext(BookerContext);
  const [isWebBooker, setIsWebBooker] = useState(
    context?.state?.switchedBookingType?.isSwitched ? context?.state?.switchedBookingType?.type === 'web' : !alias,
  );
  const [step, setStep] = useState(0);
  const [stepBack, setStepBack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, key: '', buttons: [] });
  const [signupPopup, setSignupPopup] = useState({ isOpen: false, key: '', buttons: [] });
  const [signupSuccessPopup, setSignupSuccessPopup] = useState({ isOpen: false, key: 'signUpSuccess', buttons: false });
  const [setIsValid, setDoValidateForm] = useValidatedInput('btn-view', false);

  const fareTimerId = useRef(0);
  const startRidePopupTimerId = useRef(0);
  const actionBtn = useRef(null);
  const upcomingRideTimerId = useRef(0);
  const upcomingRideStatusTimerId = useRef(0);

  const [ride, setRide] = useState(null);
  const [rideParams, setRideParams] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [isNewFare, setIsNewFare] = useState(false);
  const [newFare, setNewFare] = useState(null);
  const [fareId, setFareId] = useState(null);
  const [newDropoff, setNewDropoff] = useState(null);
  const [rideStatus, setRideStatus] = useState(null);
  const [rideEta, setRideEta] = useState(null);
  const [isOpenTips, setIsOpenTips] = useState(false);
  const [resetMapHeight, setResetMapHeight] = useState(false);
  const [isWebBookerPortrait, setIsWebBookerPortrait] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [doClearRide, setDoClearRide] = useState(false);
  const [isTipsOpen, setIsTipsOpen] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const [updatePaymentsMenu, setUpdatePaymentsMenu] = useState(false);
  const [isUpcomingRideStatusChanged, setIsUpcomingRideStatusChanged] = useState(0);
  const isTokenStored = useRef(null);

  useEffect(() => {
    setIsProcessing(false);
    isTokenStored.current = getToken();
    const isTabJustLoaded = sessionStorage.getItem('is-tab-just-loaded');
    if (isTabJustLoaded) return;

    setLoading(true);
    sessionStorage.setItem('is-tab-just-loaded', JSON.stringify(true));
    const { rideStatusRecordedFlags, isWebBooker } = state || {};
    if (rideStatusRecordedFlags?.isRideActive && ((alias && !isWebBooker) || (!alias && isWebBooker))) {
      sessionStorage.setItem('do-check-ride', JSON.stringify(true));
      setLoading(false);
      return;
    }

    clearBooker();
  }, []);

  useEffect(() => {
    const deviceId = localStorage.getItem('deviceId');
    if (deviceId && deviceId !== 'null') return;
    const ravelin = new Ravelin({ key: 'publishable_key_...' });
    ravelin.core.id().then(function (id) {
      updateDeviceId(id);
    });
  }, []);

  useEffect(() => {
    updateCustomToken(customTokenFromURL);

    if (state.isWebBooker === isWebBooker) return;

    QRInputKeyList = InputKeyList[isWebBooker ? 'web' : 'quest'];
    QRBtnHandlerList = BtnHandlerList[isWebBooker ? 'web' : 'quest'];
    updateIsWebBooker(isWebBooker);
  }, [isWebBooker]);

  useEffect(() => {
    const { code } = store.getState() || {};

    if (customTokenFromURL) {
      authService.setDelegationToken(customTokenFromURL);
      const userIDFromToken = authService.getUserIdFromToken(customTokenFromURL);
      updateUserId(userIDFromToken);
      console.log(userIDFromToken);
      if (code?.isVerified || JSON.parse(localStorage.getItem('step')) < 5) {
        updatePickup('');
        updateDropoff('');
        updateStep(5);
        getActiveRide();
      }
      return;
    }

    if (code?.isVerified) return;

    if (!alias !== isWebBooker) setIsWebBooker(!alias);
    if (!alias && !isTokenStored.current?.isTokenForRider() && state.step > 0) updateStep(!alias ? 0 : 1);

    if (alias) updateStep(1);
    else return;

    if (state.step === 0) {
      QRBtnHandlerList = BtnHandlerList[!alias ? 'web' : 'quest'];
      onNextHandler();
    } else getPickupLocation();
  }, [alias]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state.step]);
  useEffect(() => {
    if (isWebBooker) {
      if (state.step == 0) {
        mixpanelEventTrack('openLandingPageWebBook');
      }
      if (state.step === 7 || state.step === 11) {
        setDoValidateForm(false);
        setIsValid(true);
      }
    }
  }, [state.step, isWebBooker]);
  useEffect(() => {
    if (state.rideId) return;

    if (!isWebBooker && state.fare) updateFare('');
    const { isBookerCleared } = store.getState();
    if (isBookerCleared && (state.pickup || state.dropoff)) {
      if (state.pickup && !state.dropoff) store.dispatch({ type: 'updateIsBookerCleared', isBookerCleared: false });
      return;
    }

    if (!state.pickup || !state.dropoff || (isWebBooker && state.step === 5)) return;

    if (!isWebBooker) estimateFare();
  }, [state.pickup]);
  useEffect(() => {
    if (state.rideId) return;

    if (!isWebBooker && state.fare) updateFare('');
    if (!state.pickup || !state.dropoff) return;

    const { isBookerCleared } = store.getState();
    if (isBookerCleared && (state.pickup || state.dropoff)) {
      store.dispatch({ type: 'updateIsBookerCleared', isBookerCleared: false });
      return;
    }

    if (!isWebBooker) estimateFare();
  }, [state.dropoff]);
  useEffect(() => {
    if (JSON.parse(localStorage.getItem('step')) !== state.step) return;

    if (fareTimerId.current) clearTimeout(fareTimerId.current);
    if (!state.fare || state.fare.isExpired || state.editor || !QRInputKeyList[state.step || 0]?.includes('fare'))
      return;

    fareTimerId.current = setTimeout(() => {
      clearTimeout(fareTimerId.current);
      if (
        !(state.pickup && state.dropoff && state.fare) ||
        state.editor ||
        !QRInputKeyList[state.step || 0]?.includes('fare')
      )
        return;
      updateFare({ ...state.fare, isExpired: true });
    }, 60000);
  }, [state.fare, state.step]);
  useEffect(() => {
    if (!state.editor) {
      setStepBack(null);
      return;
    }
    const { key, previousKey, isOpenedFromMenu } = state.editor || {};
    if (key === 'country' && !state.inst) {
      updateEditor(isOpenedFromMenu ? { key: previousKey, isOpenedFromMenu } : '');
    }

    if (!isWebBooker && key === 'payment') {
      updateEditor('');
      setStepBack(state.step + 1);
      updatePaymentMethodId(null);
    }
  }, [state.editor]);
  useEffect(() => {
    updateError(null);
  }, [store.getState()?.code]);
  useEffect(() => {}, [state.paymentListId]);
  useEffect(() => {
    if (JSON.parse(localStorage.getItem('step')) !== state.step) return;

    setError(null);
    if (!isWebBooker) {
      if (state.step === 1) {
        updateCode('');
        updateError(null);
      }
      if (state.step === 6) {
        getProviders();
        requestRide();
      }
      if (state.step === 8) {
        updatePickup('');
        updateDropoff('');
      }
      if (state.step === 3 && state.rideLater?.isLaterRideBooked && !state.isUpcomingRidePopup) {
        openUpcomingRidePop();
      }
      if (state.step === 1 || state.step === 3) {
        getUserLocation();
        getUpcomingRides();
        setLoading(false);
      }
      if (state.step === 3) mixpanelEventTrack('openPickupDropoffPage');
    } else {
      if (state.step === 8) {
        getServiceTypesFareUserProfile();
        getProviders();
        if (!state.payment?.id) getPayments();
      }
      if (state.step === 9) requestRide();

      if (isWebBooker && state.step === 5 && state.rideLater?.isLaterRideBooked && !state.isUpcomingRidePopup) {
        openUpcomingRidePop();
      }
      if (state.step === 5) {
        getUserLocation();
        getUpcomingRides();
        setLoading(false);
      }
      if (state.step === 11) {
        updatePickup('');
        updateDropoff('');
      }

      if (state.step === 5) mixpanelEventTrack('openPickupDropoffPageWebBook');

      setStep(state.step);
    }

    setStep(state.step);
  }, [state.step]);
  useEffect(() => {
    if (!state.selectedFleet || state.editor) return;

    getPayments();
  }, [state.selectedFleet]);
  useEffect(() => {
    if (!state.paymentMethodId) return;

    setLoading(false);
  }, [state.paymentMethodId]);
  useEffect(() => {
    const { rideStatusRecordedFlags } = state || {};
    const mixpanelEvents = JSON.parse(localStorage.getItem('mixpanelEvents')) || {};
    const isTabJustLoaded = sessionStorage.getItem('is-tab-just-loaded');
    const doCheckRide = sessionStorage.getItem('do-check-ride');
    if (!rideParams?.rideId || rideParams?.rideId !== state.rideId || state.editor) {
      if (isTabJustLoaded && !doCheckRide && rideStatusRecordedFlags?.isRideActive) {
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: false });
      }
      return;
    }

    setLoading(false);
    if (
      rideStatus === 'reassigning' ||
      rideStatus === 'provider_submitted' ||
      rideStatus === 'riderway_submitted' ||
      rideStatus === 'manual_dispatch' ||
      rideStatus === 'submitted'
    ) {
      if (!mixpanelEvents.rideSubmitted) {
        mixpanelEventTrack(isWebBooker ? 'rideSubmittedWebBook' : 'rideSubmitted');
        mixpanelEvents.rideSubmitted = true;
        updateMixpanelEvents(mixpanelEvents);
      }

      if (!rideStatusRecordedFlags?.isRideActive)
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: true });
    }
    if (
      rideStatus === 'submitted' ||
      rideStatus === 'provider_submitted' ||
      rideStatus === 'assigned' ||
      rideStatus === 'dispatched' ||
      rideStatus === 'dispatch_pending' ||
      rideStatus === 'on_site'
    ) {
      if (rideParams?.rideStatusMessage === 'Ride not found.') {
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: false });
        return;
      }
      setResetMapHeight(true);

      if (rideStatus === 'on_site' && rideParams?.rideStatusMessage !== 'Ride not found.') {
        if (!mixpanelEvents.rideArrived) {
          mixpanelEventTrack(isWebBooker ? 'rideArrivedWebBook' : 'rideArrived');
          mixpanelEvents.rideArrived = true;
          updateMixpanelEvents(mixpanelEvents);
        }
      }

      if (rideStatus === 'assigned') {
        if (!rideStatusRecordedFlags?.isRideAssignRecorded) {
          if (!mixpanelEvents.rideAssigned) {
            mixpanelEventTrack(isWebBooker ? 'rideAssignedWebBook' : 'rideAssigned');
            mixpanelEvents.rideAssigned = true;
            updateMixpanelEvents(mixpanelEvents);
          }
          updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideAssignRecorded: true });
        }
      }
      if (!rideStatusRecordedFlags?.isRideActive)
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: true });
    }
    if (rideStatus === 'enroute') {
      if (isWebBooker && !state.isRideStartedPopup) {
        FetchService.getWebApiResource(urlService.getProfile())
          .then(response => {
            const { user_profile: { default_tip = null, default_tip_type = '' } = {} } = response || {};
            updateDefaultTips({
              default_tip: default_tip_type === 'percent' ? default_tip * 100 : default_tip / 100,
              default_tip_type,
              default_selected: false,
            });
          })
          .finally(() => {
            openTipsOnEnrouteStatusRide(rideStatusRecordedFlags);
          });
      } else {
        openTipsOnEnrouteStatusRide(rideStatusRecordedFlags);
      }
    }
    if (rideStatus === 'review' || rideStatus === 'missed' || rideStatus === 'canceled') {
      if (!mixpanelEvents.rideMissedCanceledBySystem) {
        mixpanelEventTrack(isWebBooker ? 'rideMissedCanceledBySystemWebBook' : 'rideMissedCanceledBySystem');
        mixpanelEvents.rideMissedCanceledBySystem = true;
        updateMixpanelEvents(mixpanelEvents);
      }
      if (rideStatusRecordedFlags?.isRideAssignRecorded) {
        updateRideStatusRecordedFlags({ isRideAssignRecorded: false });
      }
      if (rideStatusRecordedFlags?.isRideActive)
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: false });

      updatePickup('');
      updateDropoff('');
      clearRideHandler();
      openMissedRidePopup();
    }

    if (rideStatus === 'completed') {
      if (state.accountCreation?.flow) {
        const { emailAdded = false } = state.accountCreation || {};
        updateAccountCreation({ emailAdded });
      }
      togglePopup(false, popup?.key);
      clearTimeout(startRidePopupTimerId.current);
      startRidePopupTimerId.current = 0;
      updateIsRideStartedPopup('');
      updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideAssignRecorded: false });

      if (!rideStatusRecordedFlags?.isRideCompleteRecorded) {
        if (!mixpanelEvents.rideCompleted) {
          mixpanelEventTrack(isWebBooker ? 'rideCompletedWebBook' : 'rideCompleted');
          mixpanelEvents.rideCompleted = true;
          updateMixpanelEvents(mixpanelEvents);
        }
        updateRideStatusRecordedFlags({ isRideCompleteRecorded: true });
      }
      if (rideStatusRecordedFlags?.isRideActive) {
        updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: false });
      }
      if (!doCheckRide && ((isWebBooker && step === 10) || (!isWebBooker && step === 7))) {
        toggleTips(false);

        if (!isProcessing) {
          checkPaymentStatus();
        }
        setIsProcessing(true);
      }
    }
  }, [rideParams, rideStatus, rideEta]);
  useEffect(() => {
    const actionBtn = document.getElementsByClassName('btn-view')[0];
    const actionBtnHeight = actionBtn?.offsetHeight;

    const root = document.documentElement;
    root?.style.setProperty('--action-btn-height', `${actionBtnHeight}px`);

    if (!isiOS15Safari()) return;

    root?.style.setProperty('--position-btn-view', 'sticky');
    root?.style.setProperty('--container-height', '100vh');
    root?.style.setProperty('--fare-bottom', `${actionBtnHeight}px`);
    root?.style.setProperty('--privacy-bottom', `${actionBtnHeight + 20}px`);
  }, []);
  useEffect(() => {
    if (!isWebBooker) return;

    const onChangePortraitOrientation = e => {
      onPortraitChange(e);
    };
    const mediaQueryList = window.matchMedia('(max-width: 768px)');
    onPortraitChange(mediaQueryList);
    mediaQueryList.addEventListener
      ? mediaQueryList.addEventListener('change', onChangePortraitOrientation, false)
      : mediaQueryList.addListener(onChangePortraitOrientation);

    return () => {
      mediaQueryList.removeEventListener
        ? mediaQueryList.removeEventListener('change', onChangePortraitOrientation, false)
        : mediaQueryList.removeListener(onChangePortraitOrientation);
    };
  }, [isWebBooker]);
  useEffect(() => {}, [state.country]);
  useEffect(() => {
    const {
      isWebBooker,
      code: { isCurbAccount } = {},
      isRideStartedPopup,
      accountCreation,
      accountCreation: { done, isShown, emailAdded = false } = {},
      editor,
      step,
    } = state || {};
    const flag = !isWebBooker && !isCurbAccount && !isProcessing;
    const doSignupOfferPre = flag && !accountCreation && step < 8 && !editor && isRideStartedPopup;
    const doSignupOfferPost = flag && !done && !isShown && step === 8;
    let { isOpen, key } = signupPopup || {};
    const { [key]: { isOpen: popupStoreIsOpen = false } = {} } = store.getState().popup || {};

    if (!(doSignupOfferPre || doSignupOfferPost)) {
      if (isOpen || popupStoreIsOpen) {
        setSignupPopup({ isOpen: false });
        togglePopup(false, key);
      }
      return;
    }
    const clickHandler = (flow, key) => {
      updateAccountCreation({
        flow,
        step: 0,
        type: key === 'signUpOfferPre' ? 'pre' : 'post',
        ...(key === 'signUpOfferPost' && { emailAdded }),
      });
      mixpanelEventTrack(flow === 'quest' ? 'continueAsGuestBannerTapped' : 'signUpBannerTapped');
      setSignupPopup({ isOpen: false });
      togglePopup(false, key);
    };
    key = doSignupOfferPre ? 'signUpOfferPre' : 'signUpOfferPost';
    let signUpOfferBtn = [
      {
        clickHandler: () => clickHandler('signUp', key),
        textBtn: 'Sign Up',
      },
    ];
    if (doSignupOfferPre)
      signUpOfferBtn = [
        {
          clickHandler: () => clickHandler('quest', key),
          textBtn: 'Continue as a guest',
        },
        ...signUpOfferBtn,
      ];
    if (!isOpen || !popupStoreIsOpen) {
      const signupPopupParams = {
        isOpen: true,
        key,
        buttons: signUpOfferBtn,
      };
      setSignupPopup({ ...signupPopupParams });
      togglePopup(true, signupPopupParams.key, signupPopupParams.buttons);
    }
  }, [state, updateAccountCreation, step, isProcessing]);
  useEffect(() => {
    const { isWebBooker, accountCreation: { done, shown } = {} } = state || {};
    const { isOpen, key } = signupSuccessPopup || {};
    const doSignupSuccess = isWebBooker && done && !shown && step === 5;
    if (!doSignupSuccess) {
      if (isOpen) {
        setSignupSuccessPopup({ ...signupSuccessPopup, isOpen: false });
        togglePopup(false, key);
      }
      return;
    }

    if (!isOpen) {
      setSignupSuccessPopup({ ...signupSuccessPopup, isOpen: true });
      togglePopup(true, key, signupSuccessPopup.buttons);
    }
  }, [state, step]);
  useEffect(() => {
    const { isOpen, key } = signupSuccessPopup || {};
    if (!isOpen) return;

    const timer = setTimeout(() => {
      setSignupSuccessPopup({ ...signupSuccessPopup, isOpen: false });
      togglePopup(false, key);
      updateAccountCreation({ ...state.accountCreation, shown: true });
    }, 3000);

    return () => clearTimeout(timer);
  }, [signupSuccessPopup]);
  useEffect(() => {
    const mapContainer = document.getElementById('map');
    if (state.accountCreation?.flow) mapContainer?.classList?.add('hidden');
    else mapContainer?.classList?.remove('hidden');
  }, [state.accountCreation]);
  useEffect(() => {
    const { rideStatusRecordedFlags } = state || {};
    const doCheckRide = sessionStorage.getItem('do-check-ride');
    if (!doCheckRide || rideStatusRecordedFlags?.isRideActive) return;

    sessionStorage.removeItem('do-check-ride');
    clearBooker();
  }, [state.rideStatusRecordedFlags]);

  useEffect(() => {
    if (upcomingRideStatusTimerId.current) clearTimeout(upcomingRideStatusTimerId.current);
    if (!state.rideLater || !state.rideLater.rideId) return;

    upcomingRideStatusTimerId.current = setTimeout(() => {
      clearTimeout(upcomingRideStatusTimerId.current);
      if (!state.rideLater.time || !state.rideLater.date) return;
      setIsUpcomingRideStatusChanged(isUpcomingRideStatusChanged + 1);

      FetchService.getWebApiResource(urlService.getRide(state.rideLater.rideId))
        .then(response => {
          const { status } = response;
          if (status.status_code === 'canceled' || status.status_code === 'completed') {
            updateRideLater({});
            setIsUpcomingRideStatusChanged(0);

            if (isWebBooker && state.step === 13) {
              updateStep(5);
            }
          }
          return;
        })
        .catch();
    }, 10000);
  }, [isUpcomingRideStatusChanged]);

  useEffect(() => {
    if (upcomingRideTimerId.current) clearTimeout(upcomingRideTimerId.current);
    if (!state.rideLater) return;

    upcomingRideTimerId.current = setTimeout(() => {
      clearTimeout(upcomingRideTimerId.current);
      if (!state.rideLater.time || !state.rideLater.date) return;
      updateRideLater({ ...state.rideLater });

      const nowDateTime = new Date().toString().split(' ');
      const nowDate = nowDateTime[1] + ' ' + nowDateTime[2];
      const nowTime = new Date();

      if (state.rideLater?.date?.trim() !== nowDate) return;

      updateRideLater({ ...state.rideLater });

      const puTime = state.rideLater.pickupTime.pickup_time_in_millis || state.rideLater.pickupTime.pickup_time;
      const rideLaterPickupTime = new Date(puTime);
      let offerSendingTime = new Date(puTime);
      offerSendingTime = offerSendingTime.setMinutes(rideLaterPickupTime.getMinutes() - 15);
      const isUpcomingRideOfferSent = offerSendingTime <= nowTime && nowTime <= rideLaterPickupTime;
      if (isUpcomingRideOfferSent && (!ride?.id || state.rideLater.rideId === ride?.id)) {
        FetchService.getWebApiResource(urlService.getRideDetails(state.rideLater.rideId))
          .then(response => {
            const rideObj = response.ride;
            setRide({ ...rideObj });
            updateRideId(state.rideLater.rideId);
            const { id: rideId, rideStatusMessage } = rideObj || {};
            setRideParams({ rideId, rideStatusMessage });
            updateRideLater({});
            updateStep(isWebBooker ? 10 : 7);
            return;
          })
          .catch(handleError);
      }

      if (isUpcomingRideOfferSent && ride?.id && state.rideLater.rideId !== ride.id) {
        FetchService.getWebApiResource(urlService.getRideDetails(state.rideLater.rideId))
          .then(response => {
            const { ride } = response;
            if (ride.state.includes('cancel')) {
              togglePopup(true, 'canceledUpcomingRideBySystem');
              updateRideLater({});
            }
          })
          .catch(handleError);
      }

      setLoading(false);
    }, 60000);
  }, [state.rideLater]);
  useEffect(() => {
    const { isOpen, key } = popup;
    const keyFare = 'fare';
    if (isOpen && key === keyFare) return;

    let popupList = store.getState()?.popup || {};
    if (!popupList.hasOwnProperty(keyFare)) return;

    popupList = Object.keys(popupList)?.reduce((obj, k) => {
      return k === keyFare ? { ...obj } : { ...obj, [k]: popupList[k] };
    }, {});
    store.dispatch({ type: 'updatePopup', popup: popupList });
  }, [popup]);

  const rideLater = context?.state?.rideLater;

  let inst = null; // don't refactor this param. we need it for apple pay
  let checkPaymentTimerIdList = [];
  let QRInputKeyList = InputKeyList[isWebBooker ? 'web' : 'quest'];
  const isStartedAsGuest = store.getState().isStartedAsGuest;

  const getPickupLocation = () => {
    if (!alias) return false;
    const { step } = state;
    if (step !== 4) setLoading(true);
    updatePickup('');
    return FetchService.getPickupLocation(urlService.universalLabel(alias))
      .then(response => {
        const { name, city, state, country, postal_code, latitude, longitude, line1, logo_url } = response || {};
        const pickupText = `${line1}, ${city}, ${state} ${postal_code}`;
        const simple = {
          name,
          line1,
          city,
          state,
          country,
          postal_code,
          latitude,
          longitude,
          logo_url,
        };
        updatePickup({ text: pickupText, simple, alias });
        store.dispatch({
          type: 'updateIsStartedAsGuest',
          isStartedAsGuest: true,
        });
        setLoading(false);
        registerMixpanelEvent(name);
        if (step === 0 || step === 1) mixpanelEventTrack('openLandingPage');

        return !!pickupText?.length;
      })
      .catch(handleError);
  };

  const initWebOTP = () => {
    if (!isAndroid()) return;

    if ('OTPCredential' in window) {
      const ac = new AbortController();
      console.log('ac ', ac);
      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then(otp => {
          console.log('then otp ', otp);
          const { code } = otp;
          console.log('otp - updateCode');
          updateCode({ value: code, isOTP: true });
          ac.abort();
        })
        .catch(err => {
          console.log(err);
        });
    }
  };

  const requestCode = () => {
    mixpanelEventTrack(isWebBooker ? 'getCodeWebBook' : 'getCode');

    const { phone = {} } = store.getState();
    const { number, requestNumber = 0 } = phone;
    if (!number) return false;
    if (requestNumber >= 4) {
      store.dispatch({
        type: 'updatePhone',
        phone: { ...phone, requestNumber: requestNumber + 1 },
      });
      return false;
    }

    setLoading(true);
    return FetchService.getResource(urlService.challenge(number))
      .then(responseChallenge => {
        const { challenge } = responseChallenge;
        return FetchService.getResource(urlService.code(number, challenge))
          .then(response => {
            const { token } = response || {};
            //initWebOTP();
            authService.setVerificationToken(token);
            store.dispatch({
              type: 'updatePhone',
              phone: { ...phone, requestNumber: requestNumber + 1 },
            });
            store.dispatch({
              type: 'updateCode',
              code: '',
            });
            setLoading(false);
            return !!token;
          })
          .catch(handleError);
      })
      .catch(handleError);
  };

  const isCurbAccountExist = () => {
    return FetchService.getWebApiResource(urlService.getProfile())
      .then(response => {
        const {
          user_profile: {
            default_tip = null,
            default_tip_type = '',
            user_attributes,
            sms_enabled,
            user_attributes: { email, id } = {},
          } = {},
        } = response || {};
        console.log('user_attributes', user_attributes);
        console.log('email', email);
        user_attributes['sms_enabled'] = sms_enabled;
        updateUserId(id);
        updateUserProfile(user_attributes);
        updateDefaultTips({
          default_tip: default_tip_type === 'percent' ? default_tip * 100 : default_tip / 100,
          default_tip_type,
          default_selected: false,
        });
        return !!email;
      })
      .catch(handleError);
  };

  const verifyCode = () => {
    mixpanelEventTrack(isWebBooker ? 'codeConfirmedWebBook' : 'codeConfirmed');

    const { phone, code } = store.getState();
    const verificationToken = authService.getVerificationToken();
    const deviceObj = {
      os: getOS(),
      browser: getBrowserVersion(),
      user_agent: getUserAgent(),
      device_identifier: JSON.parse(localStorage.getItem('deviceId')) || null,
    };
    if (!(phone?.number && verificationToken && code?.value)) return false;
    setLoading(true);
    return FetchService.getResource(urlService.verify(phone?.number, verificationToken, code?.value, deviceObj))
      .then(async response => {
        const { new_user, token } = response || {};
        authService.setDelegationToken(token);
        const isCurbAccount = await isCurbAccountExist();
        store.dispatch({
          type: 'updateCode',
          code: { ...code, isVerified: true, isCurbAccount },
        });
        updateError(null);
        console.log('isCurbAccount', isCurbAccount);
        setLoading(false);
        //getUpcomingRides();
        return token ? [token, isCurbAccount] : false;
      })
      .catch(handleError);
  };

  const openPayment = () => {
    mixpanelEventTrack(isWebBooker ? 'paymentMethodWebBook' : 'continueTapped');
    const { paymentListId, previousStep, oneTimePaymentId, editor } = state;

    if (oneTimePaymentId) {
      if (oneTimePaymentId === 'applePay' || oneTimePaymentId === 'payPal') {
        updateEditor('');
        updatePaymentMethodId('');
        updatePayment({
          description: oneTimePaymentId === 'applePay' ? 'Apple Pay' : 'PayPal',
          type: oneTimePaymentId === 'applePay' ? 'apple_pay' : 'paypal',
        });
        updateStep(previousStep);
        updatePreviousStep('');
        updatePaymentListId(oneTimePaymentId);
      } else {
        updatePaymentListId('card');
        if (!editor?.isOpenedFromMenu) updateEditor('');
        updatePayment('');
        updateStep(isWebBooker ? 7 : 5);
      }
      updateOneTimePaymentId(null);
      // updatePaymentListId(null);
      return;
    }

    if (previousStep && paymentListId !== 'card') {
      updateEditor('');
      updateStep(previousStep);
      updatePreviousStep('');
      if (paymentListId !== 'applePay' && paymentListId !== 'payPal') updatePaymentListId(null);
      return;
    }

    if (paymentListId === 'card') {
      updateEditor('');
      updateStep(step + 1);
      updatePaymentListId(null);
      return;
    }

    if (paymentListId === 'applePay') {
      updatePayment({ description: 'Apple Pay', type: 'apple_pay' });
    }

    if (paymentListId === 'payPal') {
      updatePayment({ description: 'PayPal', type: 'paypal' });
    }

    updateEditor('');
    updateStep(step + 2);
  };

  const saveCard = card => {
    if (card) {
      updatePayment(card);
      return true;
    }

    mixpanelEventTrack(isWebBooker ? 'addCardDoneWebBook' : 'nextTapped');

    const inst = state.inst;

    if (inst.validateCardForm()) {
      setLoading(true);
      setIsCardValid(true);
      card = inst.collectCardData();
      updatePayment(card);
      inst.getVeripayToken();
    }
  };

  const setDestination = () => {
    if (state.accountCreation?.done) {
      updateStep(state.step + 1);
    } else {
      updateStep(8);
    }
    // getServiceTypesFareUserProfile();
  };

  const estimateFare = service_type => {
    const { simple: pickup, alias } = state.pickup || {};
    const { simple: dropoff } = state.dropoff || {};

    if (!(pickup && dropoff)) return;

    if (!service_type) service_type = state.defaultServiceType;

    setError(null);
    updateError(null);
    if (!isWebBooker) updateFare('');
    if (state.step !== 4) setLoading(true);
    return FetchService.getWebApiResource(
      urlService.estimateFare({ pickupAlias: alias, pickup, dropoff, service_type }),
    )
      .then(response => {
        const { amount, fee, service_fee, total_amount, currency_code, curb_credits, estimate_id } = response || {};
        const fare = {
          amount,
          fee,
          total_amount,
          currency_code,
          estimate_id,
          service_fee,
          curb_credits,
        };
        if (!(isWebBooker && service_type)) {
          updateFare(!!total_amount ? fare : '');
          setLoading(false);
          if (!total_amount) throw new Error('We cannot estimate your ride');
        }
        if (total_amount && service_type) {
          const currState = isStorageItem('serviceTypesAndAmounts') ?? {};
          const serviceTypeFare = { [service_type]: fare };
          updateServiceTypesAndAmounts({ ...currState, ...serviceTypeFare });
          const defaultServiceType = state.defaultServiceType || isStorageItem('defaultServiceType');
          if (defaultServiceType && defaultServiceType === service_type) {
            updateFare(fare);
            updateIsMeteredFare(fare.fare_type === 'meter');
          }
        }

        if (!total_amount) throw new Error('We cannot estimate your ride');
        return total_amount;
      })
      .catch(handleError);
  };

  const estimateFareWithServiceTypes = serviceTypesObj => {
    let { estimates_token, service_types } = serviceTypesObj || {};
    let { payment, selectedFleet, customToken } = state;
    const { simple: pickup, alias } = state.pickup || {};
    const { simple: dropoff } = state.dropoff || {};

    if (!(pickup && dropoff)) return;

    setError(null);
    updateError(null);
    if (!isWebBooker) updateFare('');
    if (state.step !== 4) setLoading(true);

    if (!selectedFleet) {
      selectedFleet = JSON.parse(localStorage.getItem('selectedFleet'));
    }

    if (customTokenFromURL && customToken) {
      estimates_token = customTokenFromURL;
    }

    return FetchService.getWebApiResource(
      urlService.estimateFareWithServiceTypes({ estimates_token, selectedFleet, payment, pickup, dropoff }),
    )
      .then(response => {
        const { estimates, error_message } = response || {};
        const defaultServiceType = state.defaultServiceType || isStorageItem('defaultServiceType');
        let serviceTypeFare = {};

        if (error_message) {
          setLoading(false);
          fieldParams['estimationError'].popupText = error_message;
          togglePopup(true, 'estimationError');
          return;
        }

        for (const type of estimates) {
          const { service_type, id, fare_estimate, fare_type } = type;
          if (fare_estimate) {
            const fare = {
              amount: fare_estimate.filter(item => item.key === 'rider_fare')[0]?.amount,
              capacity: service_types.filter(item => item.name === service_type)[0]?.capacity,
              image_url: service_types.filter(item => item.name === service_type)[0]?.image_url,
              display_name: service_types.filter(item => item.name === service_type)[0]?.display_name,
              name: service_type,
              total_amount: fare_estimate.filter(item => item.key === 'total_fare')[0]?.amount,
              estimate_id: id,
              fare_type,
            };
            serviceTypeFare[service_type] = fare;
            if (defaultServiceType && defaultServiceType === service_type) {
              updateFare(fare);
              updateIsMeteredFare(fare_type === 'meter');
            }
          }
        }

        const isDefault = estimates.filter(item => item.service_type === defaultServiceType)[0];

        if (isDefault?.service_type) {
          updateDefaultServiceType(isDefault?.service_type);
        } else {
          const first = [...estimates].shift();
          updateDefaultServiceType(first.service_type);
          updateFare(serviceTypeFare[first.service_type]);
          updateIsMeteredFare(first.fare_type === 'meter');
        }

        setLoading(false);
        updateServiceTypesAndAmounts(serviceTypeFare);

        console.log(serviceTypeFare);
        return true;
      })
      .catch(handleError);
  };

  const estimateFareList = async () => {
    const { serviceTypesAndAmounts, pickup, payment } = state || {};
    const { simple } = pickup || {};
    const { latitude, longitude } = simple;
    if (!serviceTypesAndAmounts && isWebBooker) return;
    updateFare('');
    const serviceTypeList = Object.keys(serviceTypesAndAmounts);
    setLoading(true);

    return FetchService.getWebApiResource(urlService.getServiceTypes(latitude, longitude, payment?.id))
      .then(async response => {
        setLoading(true);
        return await estimateFareWithServiceTypes(response);
      })
      .catch(handleError);
    setLoading(false);
    return true;
  };

  const resetIframeToMaxHeight = () => {
    const ifr = document.getElementById('addPaymentMethodIframe');
    if (!ifr) return false;

    const frm = ifr.contentWindow.document.getElementById('payment-form');
    if (!frm) return false;

    return true;
  };

  const requestRide = async () => {
    const isTabJustLoaded = sessionStorage.getItem('is-tab-just-loaded');
    if (!isTabJustLoaded) return;
    if (customTokenFromURL) return;

    if (state.payment?.type === 'apple_pay' || state.payment?.type === 'paypal') {
      let interval = null;
      setLoading(true);

      updateCardView();

      interval = setInterval(() => {
        if (resetIframeToMaxHeight()) {
          setLoading(false);
          clearInterval(interval);
          return true;
        }
      }, 1000);
    }

    if (!state.fare) {
      if (!(state.pickup && state.dropoff)) {
        togglePopup(true, 'noFare');
        return;
      }

      if (isWebBooker) estimateFareList();
      else estimateFare();
    }
  };

  const confirmAndBookRide = async () => {
    const { customToken } = state;
    mixpanelEventTrack('confirmBookWebBook');
    store.dispatch({ type: 'updateIsPaymentDone', isPaymentDone: false });

    const { paymentListId, paymentMethodId, payment } = state;

    if ((paymentListId === 'applePay' && !paymentMethodId) || payment.type === 'apple_pay') {
      saveApplePayPayment(state.inst, isWebBooker);
      return;
    }

    if ((paymentListId === 'payPal' && !paymentMethodId) || payment.type === 'paypal') {
      savePayPalPayment(state.inst, isWebBooker);
      return;
    }

    if (!customToken && (!state.payment || !state.paymentMethodId)) {
      togglePopup(true, 'noPayments');
      return;
    }

    bookRide();
  };

  const bookRide = async () => {
    mixpanelEventTrack(isWebBooker ? 'requestRideTappedWebBook' : 'requestRideTapped');

    const { estimate_id, total_amount } = state.fare || {};
    const {
      pickup,
      dropoff,
      paymentMethodId,
      accessibleVehicle: { value: isAccessibleVehicle } = {},
      defaultServiceType,
      driverNote,
      rideLater,
      selectedFleet,
    } = state;
    const { alias } = pickup || {};

    const isStartedAsGuest = store.getState().isStartedAsGuest;

    let pickup_time, pickup_time_offset_from_local;

    if (rideLater) {
      if (rideLater.isLater && rideLater.pickupTime) {
        pickup_time = rideLater.pickupTime.pickup_time;
        pickup_time_offset_from_local = rideLater.pickupTime.pickup_time_offset_from_local;
      }
    }

    if (!(pickup && dropoff && total_amount)) return;

    setLoading(true);
    setDoClearRide(false);

    const reloadPayments = async () => {
      updatePayment({});
      updatePaymentMethodId(null);
      updateApplePayPaymentMethod(null);
      updatePayPalPaymentMethod(null);
      updatePaymentListId(null);
      updateOneTimePaymentId(null);
      await getPayments();
      setUpdatePaymentsMenu(true);
      updatePreviousStep(state.isWebBooker ? 5 : 3);
      updateEditor({ key: 'payment' });
      updateStep(state.isWebBooker ? 6 : 4);
    };

    return FetchService.getWebApiResource(
      urlService.bookRide({
        pickupAlias: alias,
        pickup,
        dropoff,
        estimate_id,
        total_amount,
        paymentMethodId,
        isAccessibleVehicle,
        defaultServiceType,
        driverNote,
        isStartedAsGuest,
        isWebBooker,
        pickup_time,
        pickup_time_offset_from_local,
        selectedFleet,
      }),
    )
      .then(async response => {
        const { id } = response || {};
        updateRideId(id);
        updateDriverNote('');
        setLoading(false);
        if (!id) {
          await reloadPayments();
          throw new Error('Sorry, we had a problem booking your ride.');
        }

        if (rideLater?.isLater) {
          updateRideLater({
            ...rideLater,
            rideId: id,
            isLaterRideBooked: true,
            isLater: false,
            fare: total_amount ? (total_amount / 100).toFixed(2) : null,
          });
          updateRideId(null);
          setRideStatus(null);
          setRideParams(null);
          updateStep(isWebBooker ? 5 : 3);
          setIsUpcomingRideStatusChanged(isUpcomingRideStatusChanged + 1);
        } else {
          updateStep(state.step + 1);
          updateStartingRideTime(new Date());
        }
        updateFare('');
        await togglePopup(false, 'fare');

        return !!id;
      })
      .catch(async error => {
        error.message =
          'We were unable to process payment for your most recent ride. Please select a different payment method or add a new one in Curb app to pay the outstanding balance.';
        await reloadPayments();
        handleError(error);
      });
  };

  const getCurbAppLink = () => {
    const { flow } = state.accountCreation || {};
    const currFlow = InputKeyList.account[flow];
    const fieldKey = currFlow[Object.keys(currFlow)?.length - 1];
    const { downloadUrlList = {} } = fieldParams[fieldKey];

    const ua = navigator.userAgent.toLowerCase();
    const device = Object.keys(downloadUrlList).reduce((d, key) => d || (ua.indexOf(key) > -1 ? key : ''), '');
    if (!device) return;

    const url = downloadUrlList[device];
    if (!url) return;

    updateDownloadCurbAppUrl(url);
    return { downloadCurbAppUrl: url };
  };

  const updateAccount = () => {
    mixpanelEventTrack('passwordConfirmedWebBook');

    const { accountCreation: { flow } = {} } = state || {};
    const {
      name,
      email: { value: email, isValid: isEmailValid },
      password: { value: password, isValid: isPasswordValid },
    } = store.getState() || {};
    if (!(name && email && isEmailValid && password && isPasswordValid && (flow || isWebBooker))) return;

    setLoading(true);
    return FetchService.getWebApiResource(urlService.updateProfile({ name, email, password }))
      .then(response => {
        setLoading(false);
        updateUserProfile(response);
        const { login_email } = response || {};
        if (!!login_email) updateIsCurbUserExist(true);
        if (!!response) updateAccountCreation({ done: true });
        return !!response;
      })
      .then(resp => {
        if (!resp) return;
        if (state.isWebBooker) return true;

        return getCurbAppLink();
      })
      .catch(handleError);
  };

  const downloadCurbApp = () => {
    if (!state?.downloadCurbAppUrl) return;

    const { downloadCurbAppUrl } = state || {};
    window.open(downloadCurbAppUrl, '_blank');
    return true;
  };

  const getDefaultServiceType = () => {
    updateDefaultServiceType('');

    return FetchService.getWebApiResource(urlService.getProfile())
      .then(response => {
        if (!response) return;
        const { default_service_type } = response.user_profile?.user_attributes;
        console.log('default_service_type', default_service_type);
        updateDefaultServiceType(default_service_type);
        return true;
      })
      .catch(handleError);
  };

  const getServiceTypesFareUserProfile = async () => {
    mixpanelEventTrack('pickupDestinationNextWebBook');
    mixpanelEventTrack('selectRideTypeTappedWebBook');
    setLoading(true);
    const { pickup, payment } = state;
    const { simple } = pickup || {};
    const { latitude, longitude } = simple;
    if (!pickup) return;

    await getDefaultServiceType();
    updateServiceTypesAndAmounts({});
    updateFare('');

    return FetchService.getWebApiResource(urlService.getServiceTypes(latitude, longitude, payment?.id))
      .then(async response => {
        setLoading(true);
        return await estimateFareWithServiceTypes(response);
      })
      .catch(handleError);
  };

  const getPayments = (setBookerContextPayment = false) => {
    const { payment, selectedFleet, customToken } = state;
    const paymentCurrent = JSON.parse(localStorage.getItem('payment')) || payment;

    if (!selectedFleet) return;

    const isApplePay = window.ApplePaySession;

    return FetchService.getWebApiResource(urlService.loadPaymentsUsingProviderId(selectedFleet.id))
      .then(response => {
        if (response?.length && !isApplePay) {
          response = response.filter(item => item.type !== 'apple_pay');
        }

        if (!response || !response?.length) {
          if (customToken) {
            updatePaymentMethodId(0);
            updatePayment({
              description: 'Cash',
              type: 'cash',
            });
          }
          return;
        }
        const defaultPayment = response.filter(item => item.default)[0];
        const isApplePayInArray = response.filter(item => item.type === 'apple_pay')[0];
        const isPayPalInArray = response.filter(item => item.type === 'paypal')[0];

        if (isApplePayInArray) updateApplePayPaymentMethod(isApplePayInArray.id);
        if (isPayPalInArray) updatePayPalPaymentMethod(isPayPalInArray.id);

        if (setBookerContextPayment || !paymentCurrent || isEmptyObject(paymentCurrent)) {
          if (defaultPayment) {
            defaultPayment.description = updatePaymentDescription(defaultPayment);
            updatePaymentMethodId(defaultPayment.id);
            updatePayment(defaultPayment);
          } else {
            let paymentFirst = response[0];
            paymentFirst.description = updatePaymentDescription(paymentFirst);
            updatePaymentMethodId(paymentFirst.id);
            updatePayment(paymentFirst);
          }
        }

        console.log('Payments has been successfully loaded ', response);
      })
      .catch(error => {
        console.log('Add payments invalid ', error);
        handleError(error);
      });
  };

  const getProviders = () => {
    const { pickup, dropoff, paymentMethodId, fleets } = state;
    if (fleets?.length || !pickup || !dropoff) return;

    const params = {
      pickup_latitude: pickup.simple.latitude,
      pickup_longitude: pickup.simple.longitude,
      service_type: 'criteria',
      payment_method_id: parseInt(paymentMethodId) === 0 ? '0' : paymentMethodId,
      pickup_time: new Date().getTime(),
      dropoff_latitude: dropoff.simple.latitude,
      dropoff_longitude: dropoff.simple.longitude,
    };

    return FetchService.getWebApiResource(urlService.getProviders(params))
      .then(response => {
        updateFleets(response);
        updateSelectedFleet(response[0]);
        if (!response || !response?.length) throw new Error(`We're sorry, Curb isn't available yet in this market.`);
        return !!response[0];
      })
      .catch(error => {
        updateFleets(null);
        handleError(error);
      });
  };

  const bookAnotherRide = () => {
    mixpanelEventTrack('bookAnotherRideWebBook');
    setLoading(false);

    updatePickup('');
    updateDropoff('');
    clearRideHandler();
  };

  const closeTab = () => {
    mixpanelEventTrack('rideCloseTapped');
    window.open('about:blank', '_self');
    window.close();
  };

  const closeEditPaymentPage = () => {
    updateStep(state.previousStep);
    updatePreviousStep('');
    updateEditor('');
  };

  const displayValue = state.serviceTypesAndAmounts
    ? state.serviceTypesAndAmounts[state.defaultServiceType]?.display_name || ''
    : 'Taxi';

  const btnSubText = rideLater?.isLater
    ? `<div class="btn-sub-text">${rideLater?.date}, ${rideLater?.timeInterval}</div>`
    : '';

  const BtnHandlerList = {
    quest: {
      0: { btnHandler: getPickupLocation, btnText: 'Get Code', backBtn: false },
      1: { btnHandler: requestCode, btnText: 'Get Code', backBtn: false },
      2: { btnHandler: verifyCode, btnText: 'Confirm', backBtn: true },
      3: { btnHandler: null, btnText: 'Enter Payment', backBtn: false },
      4: { btnHandler: openPayment, btnText: 'Continue', backBtn: true },
      5: { btnHandler: saveCard, btnText: 'Next', backBtn: true },
      6: { btnHandler: confirmAndBookRide, btnText: `${btnSubText}Request ride`, backBtn: true },
      7: { btnHandler: null, btnText: '', backBtn: false },
      8: { btnHandler: isStartedAsGuest ? closeTab : null, btnText: isStartedAsGuest ? 'Close' : '', backBtn: false },
      9: { btnHandler: null, btnText: '', backBtn: false },
      10: { backBtn: true },
    },
    account: {
      quest: {
        0: { btnHandler: () => true, btnText: 'Done', backBtn: true },
      },
      signUp: {
        0: { btnHandler: updateAccount, btnText: 'Sign Up', backBtn: true },
        1: { btnHandler: downloadCurbApp, btnText: 'Download Curb', backBtn: true },
      },
    },
    web: {
      0: { btnHandler: requestCode, btnText: 'Get Code', backBtn: false },
      1: { btnHandler: verifyCode, btnText: 'Confirm', backBtn: true },
      2: { btnHandler: null, btnText: 'Next', backBtn: false },
      3: { btnHandler: null, btnText: 'Next', backBtn: true },
      4: { btnHandler: updateAccount, btnText: 'Confirm', backBtn: true },
      5: { btnHandler: setDestination, btnText: 'Next', backBtn: !customTokenFromURL },
      6: { btnHandler: openPayment, btnText: 'Continue', backBtn: true },
      7: { btnHandler: saveCard, btnText: `${state.editor?.isOpenedFromMenu ? 'Continue' : 'Next'}`, backBtn: true },
      8: {
        btnHandler: null,
        btnText: `${btnSubText}Request ride`,
        backBtn: true,
      },
      9: {
        btnHandler: confirmAndBookRide,
        btnText: `${btnSubText}Confirm & Book ${displayValue}`,
        backBtn: true,
      },
      10: { btnHandler: null, btnText: '', backBtn: false },
      11: { btnHandler: bookAnotherRide, btnText: 'Book another ride', backBtn: false },
      12: { btnHandler: bookAnotherRide, btnText: 'Book another ride', backBtn: false },
      13: { backBtn: true },
      14: { btnHandler: null, btnText: '', backBtn: true },
      15: { btnHandler: null, btnText: 'Next', backBtn: true },
      16: { btnHandler: closeEditPaymentPage, btnText: 'Done', backBtn: true },
    },
  };
  let QRBtnHandlerList = BtnHandlerList[isWebBooker ? 'web' : 'quest'];

  const onBackHandler = () => {
    if (state.accountCreation?.flow) {
      const { flow, step, done = false, type, emailAdded = false } = state?.accountCreation || {};
      if (!(flow === 'signUp' && step === Object.keys(InputKeyList.account[flow])?.length - 1)) {
        dispatch({
          type: 'updateName',
          name: '',
        });
        if (type === 'pre')
          dispatch({
            type: 'updateEmail',
            email: '',
          });
        dispatch({
          type: 'updatePassword',
          password: '',
        });
      }
      updateAccountCreation({ done, emailAdded: done ? true : emailAdded, ...(type === 'post' && { isShown: true }) });
      return;
    }
    if (!state.editor) {
      if (!state.isWebBooker) {
        if (state.step === 6) {
          const inst = state.inst;
          const card = inst.collectCardData();
          updatePayment(card);
        }

        if (state.step === 5 && state.previousStep === 3) {
          updateStep(state.previousStep);
          updatePreviousStep('');
          getPayments();
          return;
        }

        if (state.step === 10) {
          updateStep(3);
          return;
        }
      }

      if (state.isWebBooker && state.step === 15) {
        updateStep(4);
        return;
      }

      if ((state.isWebBooker && state.step === 7) || (!state.isWebBooker && state.step === 5)) {
        if (state.payment && !state.payment.id) {
          updatePayment({});
          updatePaymentListId('');
        }
      }

      if (state.isWebBooker && state.step === 8) {
        updateStep(state.step - 3);
        return;
      }

      if (!state.isWebBooker && state.step === 6) {
        updateStep(state.step - 2);
        return;
      }

      if (state.isWebBooker && state.step === 14) {
        dispatch({
          type: 'updateUpcomingRideLocations',
          upcomingRideLocations: '',
        });
        closeRideHistoryHandler();
        return;
      }

      if (
        state.isWebBooker &&
        (state.previousStep === 8 || state.previousStep === 9 || (state.step === 7 && state.previousStep === 5))
      ) {
        updateStep(state.previousStep);
        updatePreviousStep('');
        getPayments();
        return;
      }

      if (state.isWebBooker && state.step === 13) {
        updateStep(5);
        return;
      }

      updateStep(state.step - 1);
      return;
    }

    if (state.editor && state.isWebBooker && state.step === 5) {
      updateEditor('');
      updateStep(state.previousStep);
      updatePreviousStep('');
      return;
    }
    const { key } = state.editor || {};
    if (key === 'payment') {
      if (state.step === 7) {
        updateStep(state.step - 1);
      } else {
        updateStep(state.previousStep);
        updatePreviousStep(state.step);
        updateOneTimePaymentId(null);
        updatePaymentListId(null);
        const paymentType = `${state.payment?.description?.toLowerCase()?.replace(' ', '')}`;
        if (paymentType !== state.paymentListId?.toLowerCase()) updatePaymentListId(null);
      }
    }
    updateEditor('');
  };

  const HelperHandlerList = {
    quest: {
      code: { helperHandler: requestCode, onClosePopupHandler: onBackHandler },
      fare: { onClosePopupHandler: estimateFare },
    },
    web: {
      phone: { helperHandler: null },
      code: { helperHandler: requestCode, onClosePopupHandler: onBackHandler },
      fare: { onClosePopupHandler: estimateFareList },
    },
  };

  const QRHelperHandlerList = HelperHandlerList[isWebBooker ? 'web' : 'quest'];

  const onNextHandler = async () => {
    if (typeof state.step === 'number' && actionBtn.current?.classList.value?.includes('disabled')) return;

    setError(null);
    updateError(null);
    const result = await proceedStep();
    if (step !== 15) {
      if (state.step === Object.keys(QRInputKeyList)?.length - 1) return;
    }
    if (state.step === Object.keys(QRInputKeyList)?.length - 1) return;
    if (!result) return;
    if (isWebBooker) {
      if (step === 3) mixpanelEventTrack('emailNextWebBook');
      if (step === 8) mixpanelEventTrack('requestRideWebBook');
    } else {
      if (step === 3) mixpanelEventTrack('enterPayment');
    }
    if (state.accountCreation?.flow) {
      const { flow, step, done = false, type, emailAdded } = state.accountCreation || {};
      if (flow === 'quest') {
        updateAccountCreation({ emailAdded: true });
        mixpanelEventTrack('emailAsGuestEntered');
        return;
      }
      if (step === 0) {
        mixpanelEventTrack('nameSignupEntered');
        mixpanelEventTrack('loginEmailSignupEntered');
        mixpanelEventTrack('passwordSignupEntered');
        mixpanelEventTrack('repasswordSignupEntered');
        if (!result?.downloadCurbAppUrl) {
          updateAccountCreation({ done: true });
        } else {
          updateAccountCreation({
            flow,
            step: step + 1,
            type,
            done: true,
            emailAdded: true,
          });
        }
      } else {
        updateAccountCreation({ done: true });
        mixpanelEventTrack('downloadCurbTapped');
      }
      return;
    }

    if (isArray(result)) {
      const [token, isCurbAccount] = result || [];
      if (isCurbAccount) {
        setIsWebBooker(true);
        if (!isWebBooker)
          store.dispatch({ type: 'updateSwitchedBookingType', switchedBookingType: { isSwitched: true, type: 'web' } });
        updateIsCurbUserExist(true);
        //getUpcomingRides();
      }

      const userID = state.userId || JSON.parse(localStorage.getItem('userId'));

      return FetchService.getWebApiResource(urlService.gerUserRides({ filterBy: 'active', userId: userID }))
        .then(response => {
          const { rides } = response;

          if (rides?.length) {
            const selectedRide = rides[0];

            updatePickup({
              simple: {
                latitude: selectedRide.pickup_latitude,
                longitude: selectedRide.pickup_longitude,
              },
              text: selectedRide.pickup_location,
            });
            updateDropoff({
              simple: {
                latitude: selectedRide.dropoff_latitude,
                longitude: selectedRide.dropoff_longitude,
              },
              text: selectedRide.dropoff_location,
            });
            updateRideId(selectedRide.ride_id);
            updateStep(isCurbAccount ? 10 : 7);
          } else {
            updateStep(isCurbAccount ? 5 : isWebBooker ? 2 : 3);
          }
        })
        .catch(() => {
          updateStep(isCurbAccount ? 5 : isWebBooker ? 2 : 3);
        });
    }

    if (isWebBooker && step === 4) {
      updateStep(15);
    } else if (isWebBooker && step === 15) {
      updateStep(5);
    } else {
      updateStep(state.step + 1);
    }

    updateEditor('');
  };
  const proceedStep = () => {
    let { btnHandler } = QRBtnHandlerList[state.step];
    if (state.accountCreation?.flow) {
      const { flow, step } = state.accountCreation || {};
      btnHandler = BtnHandlerList.account[flow][step].btnHandler;
    }
    if (!btnHandler) return true;
    return btnHandler();
  };

  const togglePopup = async (isOpen, key = '', buttons = null) => {
    let popupList = store.getState()?.popup || {};
    const { [key]: { isOpen: popupStoreIsOpen = false } = {} } = popupList;
    if (!(key === 'signUpOfferPre' || key === 'signUpOfferPost' || key === 'signUpSuccess'))
      setPopup({ isOpen, ...(isOpen ? { key, buttons } : { key: '', buttons: [] }) });
    if (popupStoreIsOpen === isOpen && key?.length) return;

    const { helperHandler, onClosePopupHandler } = QRHelperHandlerList[key] || {};
    if (helperHandler && isOpen) await helperHandler();
    if (key?.length) {
      if (!isOpen) {
        popupList = Object.keys(popupList)?.reduce((obj, k) => {
          return k === key ? { ...obj } : { ...obj, [k]: popupList[k] };
        }, {});
        if (key === 'error') {
          setError(null);
          updateError('');
        }
      } else {
        popupList = { ...popupList, [key]: { isOpen, key, buttons } };
      }
    }
    store.dispatch({ type: 'updatePopup', popup: popupList });
    if (!isOpen && onClosePopupHandler) {
      if ((key === 'code' && store.getState().phone?.requestNumber > 4) || (key === 'fare' && state.fare.isExpired)) {
        onClosePopupHandler();
      }
    }
  };

  const onPortraitChange = mode => {
    if (!isWebBooker) return;

    const isPortrait = store.getState()?.isWebBookerPortrait;
    if (mode?.matches) {
      // Portrait orientation
      dispatch({ type: 'updateIsWebBookerPortrait', isWebBookerPortrait: true });
      setIsWebBookerPortrait(true);
    } else {
      // Landscape orientation
      if (isPortrait) {
        dispatch({ type: 'updateIsWebBookerPortrait', isWebBookerPortrait: false });
        setIsWebBookerPortrait(false);
      }
    }
  };

  const clearBooker = () => {
    const { keepSignedIn, userProfile, userId, rideId } = state || {};
    setDoClearRide(true);
    localStorage.clear();
    FetchService.clearDelegationToken();
    clearBookerContext();
    store.dispatch({ type: 'clearBookerStore' });
    setError(null);
    setRide(null);
    setRideStatus(null);
    setRideParams(null);
    store.dispatch({ type: 'updateIsBookerCleared', isBookerCleared: true });

    if (!alias && keepSignedIn?.value && isTokenStored.current.token && isTokenStored.current.isTokenForRider()) {
      authService.setDelegationToken(isTokenStored.current.token);
      setIsWebBooker(true);
      updateIsWebBooker(true);
      updateKeepSignedIn({ ...keepSignedIn });
      updateUserProfile({ ...userProfile });
      updateUserId(userId);
      updateRideId(rideId);
      updateIsCurbUserExist(true);
      updateStep(5);
      setLoading(false);
      return;
    }

    isTokenStored.current = null;
    if (!alias) {
      setIsWebBooker(true);
      updateIsWebBooker(true);
    }
    updateStep(!alias ? 0 : 1);
    if (alias) getPickupLocation();
    setLoading(false);
  };

  const getActiveRide = () => {
    const userID = state.userId || JSON.parse(localStorage.getItem('userId'));

    return FetchService.getWebApiResource(urlService.gerUserRides({ filterBy: 'active', userId: userID }))
      .then(response => {
        const { rides } = response;

        if (rides?.length) {
          const selectedRide = rides[0];

          updatePickup({
            simple: {
              latitude: selectedRide.pickup_latitude,
              longitude: selectedRide.pickup_longitude,
            },
            text: selectedRide.pickup_location,
          });
          updateDropoff({
            simple: {
              latitude: selectedRide.dropoff_latitude,
              longitude: selectedRide.dropoff_longitude,
            },
            text: selectedRide.dropoff_location,
          });
          updateRideId(selectedRide.ride_id);
          updateStep(10);
        }
      })
      .catch(() => {
        updateStep(5);
      });
  };

  const getUpcomingRides = () => {
    const userID = state.userId || JSON.parse(localStorage.getItem('userId'));

    return FetchService.getWebApiResource(urlService.gerUserRides({ filterBy: 'upcoming', userId: userID }))
      .then(response => {
        if (step !== 14) setLoading(false);

        if (response.rides?.length) {
          response.rides = response.rides.filter(
            item =>
              item.ride_status_code !== 'canceled' &&
              item.ride_status_code !== 'missed' &&
              item.ride_status_code !== 'completed',
          );

          if (response.rides?.length) {
            const { ride_id, dropoff_location, pickup_location, pickup_time, total, total_formatted } =
              response.rides[0];

            const result = parseISO(pickup_time).toString();
            //=> Tue Feb 11 2014 11:30:30
            const splitArray = result.split(' ');
            const time1 = new Date(pickup_time).toLocaleTimeString('en', {
              timeStyle: 'short',
              hour12: true,
            });
            const x = add(new Date(pickup_time), { minutes: 15 });
            const time2 = new Date(x).toLocaleTimeString('en', {
              timeStyle: 'short',
              hour12: true,
            });

            updateRideLater({
              date: `${splitArray[1]} ${splitArray[2]}`,
              day: 'Tomorrow',
              dropoff: { text: dropoff_location },
              isLater: false,
              isLaterRideBooked: true,
              pickup: { text: pickup_location },
              pickupTime: { pickup_time: new Date(pickup_time).getTime(), pickup_time_offset_from_local: 0 },
              rideId: ride_id,
              time: time1,
              timeInterval: `${time1} - ${time2}`,
              weekday: splitArray[0],
              fare: total,
              fare_formatted: total_formatted,
            });
          }
        }
      })
      .catch(handleError);
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (response) {
        setLoading(false);
        updateUserLocation({
          ...state.userLocation,
          latitude: response.coords.latitude,
          longitude: response.coords.longitude,
        });
      });
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  };

  const handleError = error => {
    setLoading(false);
    let { message = '', cause: { code = '', reason = '' } = {} } = error || {};
    console.error(message || error);
    console.log('handleError error ', error, ' code ', code, ' reason ', reason, ' message ', message);

    const isPhoneError = message?.includes('is not a valid phone number');
    const isCodeError = message?.includes('Not authorized');
    const errorType = isPhoneError ? 'phone' : isCodeError ? 'code' : 'global';
    const isPaymentError = reason === 'payment_method';
    const isAlreadyRideScheduledError = code.includes('You have a ride scheduled');
    if (isPaymentError) message = `${message} Please choose a different payment method.`;
    if (isAlreadyRideScheduledError) message = code;
    updateError(errorType);
    if (errorType === 'global') {
      setError(message ?? error);
      togglePopup(true, 'error');
    }

    if (state.step === 5 && state.fare) {
      setDoValidateForm(false);
      setIsValid(true);
      return;
    }
    setIsValid(false);
  };

  const openTipsOnEnrouteStatusRide = rideStatusRecordedFlags => {
    openStartRidePopup();
    if (!rideStatusRecordedFlags?.isRideActive)
      updateRideStatusRecordedFlags({ ...rideStatusRecordedFlags, isRideActive: true });
  };

  const showRide = rideObj => {
    const { id: rideId, rideStatusMessage } = rideObj || {};
    const status = rideObj.state;
    if (isStorageItem('editor')?.key === 'dropoff' && status === 'enroute') {
      return;
    }

    if (isStorageItem('editor')?.key === 'notes') {
      if (status === 'enroute') {
        updateEditor('');
        openStartRidePopup();
      }
      if (status === 'review' || status === 'missed' || status === 'canceled') {
        updateEditor('');

        updatePickup('');
        updateDropoff('');
        clearRideHandler();
        openMissedRidePopup();
      }
      return;
    }

    setRideStatus(rideObj.state);
    setRide({ ...rideObj });
    setRideParams({ rideId, rideStatusMessage });

    if (rideObj.state === 'enroute') {
      const doCheckRide = sessionStorage.getItem('do-check-ride');
      if (doCheckRide) sessionStorage.removeItem('do-check-ride');
    }
    if (!state.pickup?.simple?.line1 && rideObj?.pickup_line1) {
      updatePickup({
        simple: {
          city: rideObj.pickup_city,
          country: rideObj.pickup_country,
          latitude: rideObj.pickup_latitude,
          line1: rideObj.pickup_line1,
          longitude: rideObj.pickup_longitude,
          postal_code: rideObj.pickup_postal_code,
          state: rideObj.pickup_state,
        },
        text: state.pickup.text,
      });
    }

    if (!state.dropoff?.simple?.line1 && rideObj?.dropoff_line1) {
      updateDropoff({
        simple: {
          city: rideObj.dropoff_city,
          country: rideObj.dropoff_country,
          latitude: rideObj.dropoff_latitude,
          line1: rideObj.dropoff_line1,
          longitude: rideObj.dropoff_longitude,
          postal_code: rideObj.dropoff_postal_code,
          state: rideObj.dropoff_state,
        },
        text: state.dropoff.text,
      });
    }

    if (!state.fare?.total_amount && rideId) {
      FetchService.getWebApiResource(urlService.riderUpfrontFareForRide(rideId))
        .then(response => {
          updateFare({
            amount: response.rider_discounted_fare,
            estimate_id: response.id,
            fee: response.total_fee,
            total_amount: response.rider_fare,
          });
        })
        .catch(handleError);
    }
  };

  const showVehicle = vehicleObj => {
    if (isStorageItem('editor') && isStorageItem('editor').key) return;
    setVehicle(vehicleObj);
  };

  const hideVehicle = () => {
    if (isStorageItem('editor') && isStorageItem('editor').key) return;
    setVehicle(null);
  };

  const openMissedRidePopup = () => {
    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    const missedRideBtn = [
      {
        clickHandler: canceledRideHandler,
        textBtn: 'OK',
      },
    ];
    togglePopup(true, 'missedRide', missedRideBtn);
  };

  const openStartRidePopup = () => {
    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    if (state.isRideStartedPopup) return;
    mixpanelEventTrack(isWebBooker ? 'rideStartedWebBook' : 'rideStarted');
    updateIsRideStartedPopup(true);

    const startRideBtn = [
      {
        clickHandler: openNotInTaxiPopup,
        isSecondary: true,
        textBtn: 'NOT IN TAXI',
      },
      {
        clickHandler: openTooltip,
        textBtn: 'GOT IT',
      },
    ];
    togglePopup(true, 'startRide', startRideBtn);

    startRidePopupTimerId.current = setTimeout(() => {
      if (ride.state === 'enroute') {
        openTooltip();
      }
    }, 480000);
  };
  const openUpcomingRidePop = () => {
    updateIsUpcomingRidePopup(true);
    if (isWebBooker) updatePickup('');
    updateDropoff('');
    const upcomingRideBtn = [
      {
        clickHandler: openUpcomingRideInfo,
        textBtn: 'OK',
      },
    ];
    togglePopup(true, 'upcomingRide', upcomingRideBtn);
  };
  const openNotInTaxiPopup = () => {
    clearTimeout(startRidePopupTimerId.current);
    startRidePopupTimerId.current = 0;

    const notInTaxiBtn = [
      {
        clickHandler: openTooltip,
        isSecondary: true,
        textBtn: 'DON’T CANCEL',
      },
      {
        clickHandler: cancelRide,
        textBtn: 'CANCEL RIDE',
      },
    ];
    togglePopup(true, 'notInTaxi', notInTaxiBtn);
  };

  const openCancelRideWithFactorPopup = () => {
    const btns = [
      {
        clickHandler: removeRideCancelation,
        isSecondary: true,
        textBtn: 'DON’T CANCEL',
      },
      {
        clickHandler: cancelRide,
        textBtn: 'CANCEL RIDE',
      },
    ];

    togglePopup(true, 'cancelConfirmationRide', btns);
  };

  const removeRideCancelation = () => {
    mixpanelEventTrack(isWebBooker ? 'cancelRideDeclineWebBook' : 'cancelRideDeclineTapped');
    store.dispatch({
      type: 'updateRideCanceledParams',
      rideCanceledParams: {},
    });

    togglePopup(false);
    setPopup({ isOpen: false, key: '', buttons: [] });
  };

  const cancelRide = () => {
    mixpanelEventTrack(isWebBooker ? 'cancelRideWebBook' : 'cancelRideTapped');
    let params = null;
    const isRideAssigned =
      rideStatus === 'assigned' || rideStatus === 'dispatched' || rideStatus === 'dispatch_pending';
    const isRideArrived = rideStatus === 'on_site';
    const isRideInRide = rideStatus === 'enroute';

    const { factor } = store.getState().rideCanceledParams || {};

    if (isRideAssigned || isRideArrived) {
      if (state.initialRideEta) {
        params = {
          supports_confirmation: true,
          confirmation: !!factor,
          initial_eta_time_mins: state.initialRideEta,
        };

        if (factor) params.factor = factor;
      }
    }

    if (isRideInRide) {
      params = {
        factor: 'canceled_not_in_taxi',
      };
    }

    let { rideId } = state;

    let isUpcomingRideInfoStep = (isWebBooker && state.step === 13) || (!isWebBooker && state.step === 10);

    if (isUpcomingRideInfoStep) {
      rideId = state.rideLater.rideId;
    }

    return FetchService.getWebApiResource(urlService.cancelRide(rideId, isWebBooker ? params : null))
      .then(response => {
        if (state.initialRideEta && response.action?.factor) {
          store.dispatch({
            type: 'updateRideCanceledParams',
            rideCanceledParams: response.action,
          });

          fieldParams['cancelConfirmationRide'].popupText = response.action?.message;
          openCancelRideWithFactorPopup();
        } else {
          togglePopup(true, 'cancelRideSuccess');

          if (state.rideId === state.rideLater?.rideId) {
            updateRideLater({});
            clearRideHandler();
          }

          if (isUpcomingRideInfoStep) {
            updateRideLater({});
            updateIsUpcomingRidePopup(false);
          } else {
            clearRideHandler();
          }

          if (isWebBooker) updatePickup('');
          updateDropoff('');
          setResetMapHeight(false);
          updateStep(isWebBooker ? 5 : 3);
          setLoading(false);
          updateStartingRideTime(null);
        }
      })
      .catch(handleError);
  };

  const canceledRideHandler = () => {
    togglePopup(false, 'missedRide');

    setLoading(false);
    updatePickup('');
    updateDropoff('');
    clearRideHandler();
  };

  const clearRideHandler = () => {
    clearTimeout(startRidePopupTimerId.current);
    startRidePopupTimerId.current = 0;
    updateIsRideStartedPopup('');

    updateStep(isWebBooker ? 5 : 3);
    updateRideId(null);
    updatePayment({});
    updatePaymentMethodId(null);
    updateApplePayPaymentMethod(null);
    updatePayPalPaymentMethod(null);
    updateFleets(null);
    updateSelectedFleet(null);
    updateTipsValue({});
    updateDriverNote('');
    setRide(null);
    setRideStatus(null);
    setRideParams(null);
    setVehicle(null);
    updateRideStatusRecordedFlags({});
    setDoClearRide(true);
    updateStartingRideTime(null);
    updateMixpanelEvents({});
    if (customTokenFromURL) {
      updateCustomToken(customTokenFromURL);
      authService.setDelegationToken(customTokenFromURL);
    }
    if (isWebBooker) updateAccountCreation('');
    clearMapboxgl();
  };

  const openUpcomingRideInfo = () => {
    updateRideLater({ ...state.rideLater, isLater: false });
    setLoading(false);
    togglePopup(false, 'upcomingRide');
  };
  const openTooltip = () => {
    clearTimeout(startRidePopupTimerId.current);
    startRidePopupTimerId.current = 0;

    togglePopup(false, 'startRide');
    setIsOpenTips(true);
  };

  const toggleFare = nextState => {
    mixpanelEventTrack(isWebBooker ? 'changeDestinationCancelWebBook' : 'changeDestinationCancel');

    // if NewFareEstimate block is already opened but swipe up fired as well
    if (typeof nextState === 'boolean' && nextState) return;

    setNewDropoff(null);
    setNewFare(null);
    setFareId(null);
    setIsNewFare(typeof nextState === 'boolean' ? nextState : !isNewFare);
  };

  const changeDestination = () => {
    mixpanelEventTrack(isWebBooker ? 'changeDestinationOpenedWebBook' : 'changeDestinationOpened');
    updateEditor({ key: 'dropoff' });
  };

  const openCountryField = selectedCountry => {
    updateCountry(selectedCountry);
    const editor = isStorageItem('editor');
    const { key, previousKey, isOpenedFromMenu } = editor || {};
    updateEditor({ key: 'country', ...(isOpenedFromMenu && { previousKey: previousKey ?? key, isOpenedFromMenu }) });
  };

  const getNewFare = address => {
    setNewDropoff(address);
    updateEditor('');
    return FetchService.getWebApiResource(urlService.newFare(state.rideId, address))
      .then(response => {
        const { fare_estimate_id, new_fare, errors } = response;
        if (errors)
          throw new Error(
            `Sorry, your request to change destination wasn't successful. Please check the new address and try again.`,
          );
        updateError('');
        setIsNewFare(true);
        setNewFare(updateFloatDigits(new_fare));
        setFareId(fare_estimate_id);
      })
      .catch(handleError);
  };

  const updateDestination = () => {
    mixpanelEventTrack(isWebBooker ? 'changeDestinationOKWebBook' : 'changeDestinationOK');

    return FetchService.getWebApiResource(urlService.updateDestination(state.rideId, newDropoff.simple, fareId))
      .then(() => {
        let newFareEstimate = state.fare;
        newFareEstimate.estimate_id = fareId;
        newFareEstimate.total_amount = newFare;

        updateDropoff(newDropoff);
        updateFare(newFareEstimate);
        updateIsMeteredFare(newFareEstimate.fare_type === 'meter');
        setIsNewFare(false);
        setNewDropoff(null);
        setFareId('');

        const dropoffMarker = ride.dropoffMarker;
        if (dropoffMarker) {
          dropoffMarker.setLngLat([parseFloat(newDropoff.simple.longitude), parseFloat(newDropoff.simple.latitude)]);
        }
      })
      .catch();
  };

  const checkPaymentStatus = () => {
    const isPaymentDone = store.getState().isPaymentDone;
    if (isPaymentDone) return;

    FetchService.getWebApiResource(urlService.getRideFromList(state.rideId))
      .then(response => {
        if (isPaymentDone) return;

        const { rides } = response;
        const { bill_state } = rides[0];
        let timerId = null;
        if (bill_state === 'pending') {
          timerId = setTimeout(() => {
            checkPaymentStatus();
          }, 10000);
          checkPaymentTimerIdList = [...checkPaymentTimerIdList, timerId];
          return;
        }

        store.dispatch({
          type: 'updateIsPaymentDone',
          isPaymentDone: bill_state === 'paid' || bill_state === 'failed',
        });

        if (bill_state === 'paid') {
          updateStep(isWebBooker ? 11 : 8);
        } else if (bill_state === 'failed') {
          updateStep(isWebBooker ? 12 : 9);
        }
        checkPaymentTimerIdList.forEach(t => clearTimeout(t));
        updatePayment({});
        updatePaymentMethodId(null);
        setRide(null);
        setRideStatus(null);
        setRideParams(null);
        setVehicle(null);
        updateRideStatusRecordedFlags({});
        clearMapboxgl();
        setIsProcessing(false);
      })
      .catch(handleError);
  };

  const updateCardView = () => {
    const url = process.env.REACT_APP_STAGE
      ? 'https://way2i.stage.gocurb.com/public/resources/payment.js'
      : 'https://way2i.gocurb.com/public/resources/payment.js';

    loadScript(url)
      .then(() => {
        createIframeTempl();
      })
      .catch(() => {
        setIsValid(false);
        handleError({
          message: "Sorry, this page couldn't be loaded. Please check your internet connection and try again.",
        });
      });
  };

  const createIframeTempl = () => {
    const bindEvent = (element, eventName, eventHandler) => {
      if (element.addEventListener) {
        element.addEventListener(eventName, eventHandler, false);
      } else if (element.attachEvent) {
        element.attachEvent('on' + eventName, eventHandler);
      }
    };

    const callbackHandler = (err, paymentData, isProcessing) => {
      if (err) {
        setLoading(false);
        console.log('Payment is failed ', err);
        handleError({ message: err });
        return;
      }

      if (paymentData) {
        const card = inst.collectCardData({ applePay: paymentData.isApplePay, payPal: paymentData.isPayPal });
        saveCard(card);

        const { payPalPaymentMethod } = state;
        if (paymentData.isPayPal && payPalPaymentMethod) {
          bookRide();
        } else {
          addPayments({
            inst,
            updatePayment,
            updatePaymentMethodId,
            paymentData,
            setLoading,
            setIsCardValid,
            bookRide,
            state,
          });
        }
        console.log('Payment callback with success');
      }

      if (isProcessing) {
        setLoading(true);
        console.log('Payment in process');
      }
    };

    const paymentContainer = document.getElementsByClassName('payment-card-container')[0];
    if (paymentContainer) {
      paymentContainer.innerHTML = '';
    }

    const jwt = authService.getDelegationToken();
    if (!jwt) return;

    const user_id = jwtDecode(jwt).sub;
    const config = {
      font: 'Roboto',
      styles,
      applePayObj,
      payPalObj,
      container: 'payment-card-container',
      isScrollIframe: 'no',
      user_id,
      url: `${window.location.origin}/${Math.random().toString(36)}`,
    };

    inst = getPayInst(cardParams, config, callbackHandler);
    inst.renderIframeTemplate();

    updateInst(inst);

    bindEvent(window, 'message', e => {
      if (e.data?.isCountryDropdownOpen) return;

      if (e.data?.source?.includes('react-devtools') || e.origin?.includes('https://checkout.paypal.com')) return;

      if (document.getElementById('addPaymentMethodIframe')) {
        FetchService.getWebApiResource(urlService.getWay2IToken())
          .then(response => {
            const data = {
              total: getPaymentTotalData(),
              token: response.token,
            };
            document.getElementById('addPaymentMethodIframe').contentWindow.postMessage(data, '*');
          })
          .catch(() => {
            const ifr = document.getElementById('addPaymentMethodIframe');
            if (ifr) handleError(e);
            document.getElementById('addPaymentMethodIframe')?.contentWindow.postMessage('', '*');
          });
      }
    });
  };

  const closeRideHistoryHandler = () => {
    updateRideDetailsHistory(null);
    updateRideDetailsReceipt(null);
    updateEditor('');
    updateStep(state.previousStep ? state.previousStep : 5);
    updatePreviousStep('');
  };

  const toggleTips = nextState => {
    setIsTipsOpen(typeof nextState === 'boolean' ? nextState : !isTipsOpen);
  };

  const FieldContent = () => {
    if (state.editor && !state.editor?.isOpenedFromMenu) {
      if (isWebBooker && step === 6) return <></>;
      return <FieldControl fieldKey={state.editor.key} togglePopup={togglePopup} getNewFare={getNewFare} />;
    }
    if (isWebBooker) {
      if (
        (step === 6 || step === 7) &&
        (!state.editor || (state.editor?.isOpenedFromMenu && state.editor?.key !== 'country'))
      )
        return <></>;
      if (state.step === 7 && state.editor?.isOpenedFromMenu && state.editor?.key === 'country')
        return <FieldControl fieldKey={state.editor.key} />;
      if (state.step < 10 || state.step === 13 || state.step === 15) {
        if (state.step === 9 && isWebBookerPortrait) return <></>;

        return QRInputKeyList[state.step].map(key => (
          <FieldControl key={key} fieldKey={key} togglePopup={togglePopup} />
        ));
      }
      return <></>;
    } else {
      if (state.step === 9 || state.step === 10)
        return QRInputKeyList[state.step].map(key => (
          <FieldControl key={key} fieldKey={key} togglePopup={togglePopup} />
        ));
    }
    if ((step === 4 || step === 5) && !state.editor) return <></>;
    if (state.accountCreation?.flow) {
      const { flow, step } = state.accountCreation;
      return InputKeyList.account[flow][step]?.map(key => (
        <FieldControl key={key} fieldKey={key} togglePopup={togglePopup} />
      ));
    }
    if (state.step < 7)
      return QRInputKeyList[state.step || 0]?.map(key => (
        <FieldControl key={key} fieldKey={key} togglePopup={togglePopup} getNewFare={getNewFare} />
      ));
    return <></>;
  };

  const BackBtn = () => {
    if (isWebBooker && step === 5 && !state.editor) return <></>;
    if (state.isCurbUserExist && step === 5) return <></>;

    if (state.accountCreation?.flow) {
      return <CloseIcon onClick={onBackHandler} />;
    }
    if (!(QRBtnHandlerList[state.step]?.backBtn || state.editor)) return <></>;
    return <ArrowIcon onClick={onBackHandler} />;
  };

  const ActionBtn = () => {
    let btnText;
    if (state.accountCreation?.flow) {
      const { flow, step } = state.accountCreation || {};
      btnText = BtnHandlerList.account[flow][step]?.btnText;
    } else {
      if (!(QRBtnHandlerList[state.step]?.btnText && !(state.editor && state.editor.key !== 'payment'))) return <></>;
      btnText = QRBtnHandlerList[state.step]?.btnText;
    }

    if (!btnText) return <></>;

    return (
      <button
        ref={actionBtn}
        className="btn btn-view btn-primary"
        onClick={onNextHandler}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(btnText) }}
      />
    );
  };

  const shortContextValue = {
    loading,
    ride,
    rideStatus,
    vehicle,
    setIsWebBooker,
    isOpenTips,
    setIsOpenTips,
    setLoading,
    setResetMapHeight,
    isWebBookerPortrait,
    cancelRide,
    changeDestination,
    togglePopup,
    toggleTips,
    getPayments,
    updatePaymentsMenu,
    setUpdatePaymentsMenu,
    isCardValid,
    setIsCardValid,
    actionBtn,
    step,
    rideLater,
    QRInputKeyList,
    isProcessing,
  };
  const mapContextValue = {
    step: state.step,
    rideId: state.rideId,
    pickup: state.pickup,
    dropoff: state.dropoff,
    vehicle,
    upcomingRidePickup: store.getState()?.upcomingRideLocations?.pickup,
    upcomingRideDropoff: store.getState()?.upcomingRideLocations?.dropoff,
    isWebBooker,
    isWebBookerPortrait,
    isSignupOfferVisible: state.accountCreation?.flow,
    showRide,
    showVehicle,
    hideVehicle,
    setRideEta,
    initialRideEta: state.initialRideEta,
    updateInitialRideEta,
    resetMapHeight,
    doClearRide,
    isBookerApp: true,
    updateStartingRideTime,
    updatePickup,
    estimateFareList,
  };

  return (
    <BookerShortContext.Provider value={shortContextValue}>
      <MapContext.Provider value={mapContextValue}>
        <div
          className={`main-container ${isWebBooker ? 'web-' : 'guest-'}booker${isWebBookerPortrait ? ' portrait' : ''}
                      ${
                        ride &&
                        rideStatus &&
                        ((!isWebBooker && step === 7) || (isWebBooker && isWebBookerPortrait && step === 10))
                          ? rideStatus === 'enroute'
                            ? 'map-view-in-ride'
                            : 'map-view-assigned'
                          : state.editor && state.editor.key === 'notes'
                          ? 'map-view-assigned'
                          : 'form-view'
                      } ${
            state.switchedBookingType?.isSwitched && state?.switchedBookingType?.type === 'guest'
              ? 'guest-from-web'
              : ''
          }`}
        >
          <div className="main-form">
            <div
              className={`form-container page-${QRInputKeyList[state.step || 0]?.join('-')}${
                state.editor ? ' editor' : ''
              }${state.accountCreation?.flow ? ' accountCreation' : ''} ${
                loading && !state.isRideStartedPopup ? 'loading' : ''
              }`}
            >
              <div className="header">
                <BackBtn />
                <LogoIcon />
                <h1 className="landmark">landmark</h1>
              </div>
              <FieldContent />

              {isWebBooker &&
                state.step === 9 &&
                isWebBookerPortrait &&
                QRInputKeyList[state.step].map(key => {
                  if (key === 'map') {
                    return (
                      <Map
                        key={key}
                        isGuestBooking={false}
                        isMobileView={true}
                        lat={state.userLocation?.latitude || null}
                        lng={state.userLocation?.longitude || null}
                      />
                    );
                  } else {
                    if (key !== 'payment' && key !== 'rideTypeInput') {
                      return (
                        <FieldControl key={key} fieldKey={key} togglePopup={togglePopup} isWebBookerPortrait={true} />
                      );
                    }
                  }
                })}

              {isWebBooker &&
                ((state.step === 6 &&
                  (!state.editor || state.editor?.isOpenedFromMenu ? <PaymentMethods /> : <PaymentList />)) ||
                  ((!state.editor || (state.editor?.isOpenedFromMenu && state.editor?.key !== 'country')) &&
                    ((step === 7 && (
                      <Payments
                        saveCard={saveCard}
                        setLoading={setLoading}
                        openCountryField={openCountryField}
                        handleError={handleError}
                      />
                    )) ||
                      (state.step === 16 && <EditPayment />) ||
                      (state.step === 9 && <div className={'payment-card-container hidden'} />))))}
              {!isWebBooker
                ? (state.step === 4 && !state.editor && <PaymentMethods />) ||
                  (step === 5 && !(state.editor || state?.accountCreation?.flow) && (
                    <Payments
                      saveCard={saveCard}
                      setLoading={setLoading}
                      openCountryField={openCountryField}
                      handleError={handleError}
                    />
                  )) ||
                  (state.step === 6 && <div className={'payment-card-container hidden'} />) ||
                  (step === 8 && !state?.accountCreation?.flow && <Receipt />) ||
                  (step === 9 && <PaymentFailure />)
                : (step === 10 && !isWebBookerPortrait && state.rideId && !state.editor && (
                    <>
                      <RideHeader />
                      <ActiveRideDetails />
                      <RideFooter />
                    </>
                  )) ||
                  (step === 11 && <Receipt />) ||
                  (step === 12 && <PaymentFailure />) ||
                  (step === 14 && <RideHistory />)}
              {((!isWebBooker && step === 7) || (isWebBooker && step === 10 && isWebBookerPortrait)) && !state.editor && (
                <>
                  {!state?.accountCreation?.flow && <RideHeader />}

                  <Map
                    isGuestBooking={true}
                    lat={state.userLocation?.latitude || null}
                    lng={state.userLocation?.longitude || null}
                  />
                  {!state?.accountCreation?.flow && (
                    <>
                      <RideFooter />
                      <RideStatus />
                      <SlideUpScreen />
                    </>
                  )}
                </>
              )}
            </div>
            <ActionBtn />
            {isNewFare && (
              <NewFareEstimate
                newFare={newFare}
                toggleFare={toggleFare}
                togglePopup={togglePopup}
                updateDestination={updateDestination}
              />
            )}

            {isProcessing && <Processing />}
            {isTipsOpen && <TipDetails />}

            <Popup popup={signupPopup} />
            <Popup popup={signupSuccessPopup} />
            <Popup popup={popup} togglePopup={togglePopup} message={error} />
            <Loader
              loading={loading && (!state.editor?.isOpenedFromMenu || (state.editor?.isOpenedFromMenu && isCardValid))}
            />
          </div>
          {isWebBooker && !isWebBookerPortrait && (
            <Map
              isGuestBooking={false}
              zoom={15}
              lat={state.userLocation?.latitude || null}
              lng={state.userLocation?.longitude || null}
              isCoveredByLogo={
                !(
                  state.step > 4 &&
                  (state.pickup ||
                    state.dropoff ||
                    store.getState()?.upcomingRideLocations?.pickup ||
                    store.getState()?.upcomingRideLocations?.dropoff ||
                    state.userLocation?.latitude ||
                    state.userLocation?.longitude)
                )
              }
            />
          )}
          {isWebBooker && step > 4 && !customTokenFromURL && step !== 15 && <Menu isProcessing={isProcessing} />}
          {isWebBooker && step === 14 && state.rideDetailsHistory && (
            <RideDetailsHistory closeRideHistoryHandler={closeRideHistoryHandler} />
          )}
          {isWebBooker && step === 14 && state.rideDetailsReceipt && (
            <RideDetailsReceipt closeRideHistoryHandler={closeRideHistoryHandler} />
          )}
        </div>
      </MapContext.Provider>
    </BookerShortContext.Provider>
  );
};

const BookerWrapper = () => {
  return (
    <BookerProvider>
      <Provider store={store}>
        <Booker />
      </Provider>
    </BookerProvider>
  );
};

export default BookerWrapper;
